// src/components/payment-links/PaymentLinksTable.tsx
import {
  FiCopy,
  FiSend,
  FiXCircle,
  FiRotateCcw,
  FiExternalLink,
} from "react-icons/fi";
import ColumnFilter from "@/components/shared/ui/ColumnFilter";
import type { PaymentLink, PaymentLinkStatus } from "@/api/features/payment-links";

export type PaymentLinksTableFilters = {
  search: string;
  status: PaymentLinkStatus | "";
};

const STATUS_STYLES: Record<PaymentLinkStatus, string> = {
  DRAFT: "bg-gray-100 text-gray-600",
  SENT: "bg-sky-100 text-sky-700",
  PAID: "bg-emerald-100 text-emerald-700",
  EXPIRED: "bg-amber-100 text-amber-700",
  CANCELLED: "bg-zinc-100 text-zinc-600",
  FAILED: "bg-rose-100 text-rose-700",
  REFUNDED: "bg-purple-100 text-purple-700",
  PARTIALLY_REFUNDED: "bg-purple-100 text-purple-700",
};

const STATUS_OPTIONS: PaymentLinkStatus[] = [
  "DRAFT",
  "SENT",
  "PAID",
  "EXPIRED",
  "CANCELLED",
  "FAILED",
  "REFUNDED",
  "PARTIALLY_REFUNDED",
];

function StatusBadge({ status }: { status: PaymentLinkStatus }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLES[status]}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function fmtAmount(amount: string, currency: string) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${currency} ${amount}`;
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

function fmtDate(v: string | null) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : "—";
}

type Props = {
  rows: PaymentLink[];
  filters: PaymentLinksTableFilters;
  setFilters: (f: PaymentLinksTableFilters) => void;
  busyId?: number | null;
  onCopy: (row: PaymentLink) => void;
  onResend: (row: PaymentLink) => void;
  onCancel: (row: PaymentLink) => void;
  onRefund: (row: PaymentLink) => void;
};

export default function PaymentLinksTable({
  rows,
  filters,
  setFilters,
  busyId,
  onCopy,
  onResend,
  onCancel,
  onRefund,
}: Props) {
  const canResend = (r: PaymentLink) => r.status === "SENT";
  const canCancel = (r: PaymentLink) =>
    ["DRAFT", "SENT", "EXPIRED", "FAILED"].includes(r.status);
  const canRefund = (r: PaymentLink) =>
    ["PAID", "PARTIALLY_REFUNDED"].includes(r.status);

  return (
    // Square bottom: the pagination bar below completes this card. Search
    // lives in the page header; status filters from the column itself.
    <div className="overflow-hidden rounded-t-2xl border border-gray-200 bg-white">

      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <span>Status</span>
                  <ColumnFilter label="Status">
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters({
                          ...filters,
                          status: e.target.value as PaymentLinkStatus | "",
                        })
                      }
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
                    >
                      <option value="">All statuses</option>
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, " ")}
                        </option>
                      ))}
                    </select>
                  </ColumnFilter>
                </div>
              </th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  No payment links yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const busy = busyId === r.id;
                return (
                  <tr key={r.id} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">
                      {r.linkCode}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {r.customerName || "—"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.customerEmail || r.customerPhone || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {fmtAmount(r.amount, r.currencyCode)}
                      {Number(r.refundedAmount) > 0 ? (
                        <div className="text-xs text-purple-600">
                          −{fmtAmount(r.refundedAmount as string, r.currencyCode)} refunded
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="max-w-[220px] truncate px-4 py-3 text-gray-600" title={r.description}>
                      {r.description}
                      {r.reference ? (
                        <div className="text-xs text-gray-400">Ref: {r.reference}</div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {fmtDate(r.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {r.checkoutUrl ? (
                          <>
                            <IconBtn title="Copy link" onClick={() => onCopy(r)} disabled={busy}>
                              <FiCopy />
                            </IconBtn>
                            <a
                              href={r.checkoutUrl}
                              target="_blank"
                              rel="noreferrer"
                              title="Open link"
                              className="grid h-8 w-8 place-items-center rounded-md text-gray-500 hover:bg-gray-100"
                            >
                              <FiExternalLink />
                            </a>
                          </>
                        ) : null}
                        {canResend(r) ? (
                          <IconBtn title="Send email" onClick={() => onResend(r)} disabled={busy}>
                            <FiSend />
                          </IconBtn>
                        ) : null}
                        {canCancel(r) ? (
                          <IconBtn title="Cancel link" onClick={() => onCancel(r)} disabled={busy} danger>
                            <FiXCircle />
                          </IconBtn>
                        ) : null}
                        {canRefund(r) ? (
                          <IconBtn title="Refund" onClick={() => onRefund(r)} disabled={busy} danger>
                            <FiRotateCcw />
                          </IconBtn>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IconBtn({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`grid h-8 w-8 place-items-center rounded-md hover:bg-gray-100 disabled:opacity-40 ${
        danger ? "text-rose-600" : "text-gray-500"
      }`}
    >
      {children}
    </button>
  );
}
